import { CenteredLoader } from '@trackman/baseball-shared-components';
import { Button } from '@trackman/web-shared-components';
import { ReactComponent as BackIcon } from 'components/icons/back.svg';
import Img1 from 'components/images/scheduledGames/1.jpg';
import Img2 from 'components/images/scheduledGames/2.jpg';
import Img3 from 'components/images/scheduledGames/3.jpg';
import Img4 from 'components/images/scheduledGames/4.jpg';
import Img5 from 'components/images/scheduledGames/5.jpg';
import Img6 from 'components/images/scheduledGames/6.jpg';
import Img7 from 'components/images/scheduledGames/7.jpg';
import Img8 from 'components/images/scheduledGames/8.jpg';
import { ServiceTypes } from 'constants/ServiceTypes';
import { History } from 'history';
import { IApiGameLevel, IApiLeague, IApiScheduleResponse } from 'interfaces/team/IApiScheduleResponse';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { ConfigurationService } from 'services/ConfigurationService';
import mappingHelpers from 'services/helper-services/mappingHelpers/mappingHelpers';
import { canStartNewGame } from 'services/helper-services/utils';
import { useInjection } from 'services/ioc/ioc.react';
import { LineupStore } from 'stores/LineupStore';
import { SessionStore } from 'stores/SessionStore';
import { TrackingElementsStore } from 'stores/TrackingElementsStore';
import VideoUploaderStateStore from 'stores/VideoUploaderStateStore';
import { IFeatureConfigService } from '../../services/FeatureConfigService';
import { ContextStore } from '../../stores/ContextStore';
import { ContextualDataStore } from '../../stores/ContextualDataStore';
import { GameDialog } from './GameDialog/GameDialog';
import { ManualTaggingModal } from './ManualTaggingModal/ManualTaggingModal';
import {
  NoGames,
  NoGamesSubtitle,
  NoGamesTitle,
  ScheduledGamesWrapper,
  Subtitle,
  Title,
  TitleWrapper,
  TopBarWrapper,
} from './ScheduledGamesStyles';
import { ScheduledGameTile } from './ScheduledGameTile/ScheduledGameTile';

const images = [Img1, Img2, Img3, Img4, Img5, Img6, Img7, Img8];
interface IProps {
  history: History;
}

const ScheduledGames: React.FC<IProps> = observer((props) => {
  const configurationService = useInjection<ConfigurationService>(ServiceTypes.ConfigurationService);
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const contextualDataStore = useInjection<ContextualDataStore>(ServiceTypes.ContextualDataStore);
  const contextStore = useInjection<ContextStore>(ServiceTypes.ContextStore);
  const lineupStore = useInjection<LineupStore>(ServiceTypes.LineupStore);
  const videoUploaderStateStore = useInjection<VideoUploaderStateStore>(ServiceTypes.VideoUploaderStateStore);
  const featureConfigService = useInjection<IFeatureConfigService>(ServiceTypes.FeatureConfigService);
  const trackingElementsStore = useInjection<TrackingElementsStore>(ServiceTypes.TrackingElementsStore);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isScheduledGame, setIsScheduledGame] = useState<boolean>(false);
  const [game, setGame] = useState<IApiScheduleResponse>({} as IApiScheduleResponse);

  const config = configurationService.getConfigs();

  const { history } = props;

  useEffect(() => {
    setIsLoading(true);
    contextualDataStore.refreshCurrentDayWithoutSync().finally(() => {
      setIsLoading(false);
    });
  }, []);

  const allowStartingNewGame = canStartNewGame(
    sessionStore.isGameInProgress,
    !!contextStore.activeField,
    config.isBackfield,
    contextStore.isConnectedPtu,
    contextStore.isLoggedIn,
    trackingElementsStore.updating
  );

  const fetchConfig = async () => {
    await featureConfigService.fetchConfig();
  };

  useEffect(() => {
    if (!allowStartingNewGame) {
      history.push('/');
    }
    if (contextStore.isConnectedPtu) {
      fetchConfig();
    }
    // Needs to be on every page, so latest sync is set when user changes page
    contextualDataStore.setLatestSync();
  }, [contextStore.activeField, contextStore.isConnectedPtu, sessionStore.isGameInProgress]);

  useEffect(() => {
    videoUploaderStateStore.setIsVisible(true);

    return (): void => {
      videoUploaderStateStore.setIsVisible(false);
    };
  }, []);

  useEffect(() => {
    if (isScheduledGame) {
      lineupStore.setGameLevel(game.gameLevel ? { ...game.gameLevel } : ({} as IApiGameLevel));
      const league = contextualDataStore.leagues.find((league) => league.id === game.league.id);
      lineupStore.setLeague(league ? league : ({} as IApiLeague));
    } else {
      lineupStore.setGameLevel({} as IApiGameLevel);
      lineupStore.setLeague({} as IApiLeague);
    }
  }, [isScheduledGame, game]);

  const handleScheduledGameStart = (scheduledGame: IApiScheduleResponse) => {
    setIsScheduledGame(true);
    setGame(scheduledGame);
    contextualDataStore.setIsGameDialogVisible(true);
  };

  const autoTagHandler = () => {
    sessionStore.setIsOperatorlessTaggingEnabledForSelectedSession(true);
    setManualTaggingModalVisible(false);
    if (selectedScheduledGame) {
      handleScheduledGameStart(selectedScheduledGame);
    }
  };

  const tagManuallyHandler = () => {
    sessionStore.setIsOperatorlessTaggingEnabledForSelectedSession(false);
    setManualTaggingModalVisible(false);
    if (selectedScheduledGame) {
      handleScheduledGameStart(selectedScheduledGame);
    }
  };

  const [manualTaggingModalVisible, setManualTaggingModalVisible] = useState<boolean>(false);
  const [selectedScheduledGame, setSelectedScheduledGame] = useState<IApiScheduleResponse | null>(null);

  const startScheduleGameHandler = (scheduledGame: IApiScheduleResponse) => {
    if (featureConfigService.getConfig().isOperatorlessTaggingEnabled) {
      setSelectedScheduledGame(scheduledGame);
      setManualTaggingModalVisible(true);
    } else {
      handleScheduledGameStart(scheduledGame);
    }
  };

  const handleOnDissmiss = () => {
    contextualDataStore.setIsGameDialogVisible(false);
    lineupStore.resetSessionMetaDataValues();
  };

  const startNewGameHandler = () => {
    setIsScheduledGame(false);
    sessionStore.setIsOperatorlessTaggingEnabledForSelectedSession(false);
    contextualDataStore.setIsGameDialogVisible(true);
  };

  return (
    <ScheduledGamesWrapper>
      <TopBarWrapper>
        <Button
          label='Back'
          intent='tertiary'
          onClick={() => history.push('/')}
          width={108}
          startIcon={<BackIcon />}
          style={{ color: '#414141' }}
        />
        <TitleWrapper>
          <Title>Today’s games</Title>
          <Subtitle>
            {`${contextStore.activeField?.fieldName} - ` || ''}
            {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Subtitle>
        </TitleWrapper>
        <Button
          label='Create Game'
          intent='secondary'
          onClick={startNewGameHandler}
          disabled={sessionStore.isSessionStarted || !canStartNewGame}
          data-test='create-game'
          width={108}
        />
      </TopBarWrapper>
      {isLoading ? (
        <div style={{ marginTop: '150px' }}>
          <CenteredLoader size='large' />
        </div>
      ) : (
        <>
          <div>
            {contextualDataStore.scheduledGames.length === 0 && (
              <NoGames>
                <NoGamesTitle>No games today</NoGamesTitle>
                <NoGamesSubtitle>There are no games scheduled for today</NoGamesSubtitle>
              </NoGames>
            )}
            {contextualDataStore.scheduledGames.length > 0 && (
              <ScheduledGameTile
                onClickCallback={() => startScheduleGameHandler(contextualDataStore.scheduledGames[0])}
                game={contextualDataStore.scheduledGames[0]}
                backgroundImage={images[0]}
                isNextGame
                index={0}
                buttonVariant={featureConfigService.getConfig().isOperatorlessTaggingEnabled ? 'secondary' : 'primary'}
              />
            )}
          </div>
          <div>
            {contextualDataStore.scheduledGames.map((scheduledGame, index) => {
              if (index > 0) {
                return (
                  <ScheduledGameTile
                    onClickCallback={() => startScheduleGameHandler(scheduledGame)}
                    game={scheduledGame}
                    key={index}
                    backgroundImage={images[index % 8]}
                    index={index}
                  />
                );
              }
            })}
          </div>
        </>
      )}
      {contextualDataStore.isGameDialogVisible && (
        <GameDialog
          isResumeGame={false}
          visible
          dismissCallback={handleOnDissmiss}
          gameScheduleReference={isScheduledGame ? mappingHelpers.apiScheduleGameToGameScheduleReferenceMap(game) : undefined}
          strikeZones={contextualDataStore.strikeZones}
        />
      )}
      {manualTaggingModalVisible && (
        <ManualTaggingModal
          open={manualTaggingModalVisible}
          onClose={() => setManualTaggingModalVisible(false)}
          autoTagHandler={autoTagHandler}
          tagManuallyHandler={tagManuallyHandler}
        />
      )}
    </ScheduledGamesWrapper>
  );
});

export default ScheduledGames;
